/**
 * Schemata der Datendateien unter data/. Sie sind die einzige Stelle, an der die
 * Form der Inhalte festgelegt ist: `scripts/verify-data.ts` prüft damit, und
 * `src/core/data.ts` lädt damit. Ungültige Daten fallen im Build auf, nicht im Betrieb.
 */
import { z } from 'zod'

export const bilingual = z.object({
  en: z.string().min(1),
  de: z.string().min(1),
})

export const lightType = z.enum([
  'masthead',
  'sidelight-port',
  'sidelight-stbd',
  'sidelights-combined',
  'sternlight',
  'towing',
  'allround',
  'special-flashing',
  'handlamp',
])

export const lightColour = z.enum(['white', 'red', 'green', 'yellow', 'blue'])

export const lengthClass = z.enum(['ge50', 'm20to50', 'm12to20', 'm7to12', 'lt7'])

export const mount = z.enum([
  'mast-fwd',
  'mast-aft',
  'masthead-top',
  'bow',
  'stern',
  'deck',
  'yardarm-port',
  'yardarm-stbd',
  'best-seen',
])

export const lightSpec = z.object({
  type: lightType,
  colour: lightColour,
  mount,
  tier: z.number().int().min(0).max(4).optional(),
  /**
   * Seitlicher Versatz in Metern, nach Steuerbord positiv. Für Lichter, die die Ordnung
   * nebeneinander verlangt und für die es keinen eigenen Ankerpunkt gibt: die drei
   * Hecklichter des Schubverbandes stehen nach § 3.10 in einer waagerechten Linie mit
   * etwa 1,25 m Abstand.
   */
  versatzM: z.number().min(-20).max(20).optional(),
  rhythm: z.enum(['steady', 'flashing', 'special-flashing']).optional(),
  note: bilingual.optional(),
})

export const dayShape = z.object({
  kind: z.enum(['ball', 'cone-up', 'cone-down', 'cones-apex', 'diamond', 'cylinder', 'flag-a']),
  mount,
  tier: z.number().int().min(0).max(4).optional(),
  colour: lightColour.optional(),
})

export const vesselType = z.object({
  id: z.string().regex(/^[a-z0-9-]+$/),
  name: bilingual,
  lengthM: z.number().positive(),
  lengthClass,
  hull: z.enum(['cargo', 'sailing', 'trawler', 'tug', 'pilot', 'small', 'barge', 'seaplane']),
})

export const scope = z.enum(['both', 'international', 'inland'])

export const lightState = z.object({
  id: z.string().regex(/^[a-z0-9-]+$/),
  /** Regelnummer der COLREGs; in den US-Binnenregeln dieselbe Nummer. */
  rule: z.number().int().min(1).max(38),
  /**
   * Fundstellen im Korpus navrules.json: "a" für die eigene Regel, "23:a" für eine
   * andere. Jedes gezeigte Licht und jeder Signalkörper muss darin vorkommen.
   */
  cite: z.array(z.string().regex(/^(\d+:)?[a-z0-9()]+(:[a-z0-9()]+)?$/)).min(1),
  /** Wo der Zustand gilt: international, nur US-Binnen, oder beides. */
  scope: scope.default('both'),
  /** Abschnitt in 33 CFR Part 83, falls es eine US-Entsprechung gibt. */
  cfr: z.string().regex(/^83\.\d+$/).optional(),
  cfrCite: z.array(z.string().min(1)).optional(),
  title: bilingual,
  vessel: z.string().regex(/^[a-z0-9-]+$/),
  lengthClass,
  underway: z.boolean(),
  makingWay: z.boolean(),
  lights: z.array(lightSpec),
  dayShapes: z.array(dayShape),
  explanation: bilingual,
  tags: z.array(z.string().min(1)),
})

export const ialaRegion = z.enum(['A', 'B', 'both'])

/** Fundstelle in einer der beiden Seezeichen-Quellen. */
const citeGroup = z.array(
  z.object({ section: z.string().min(1), ids: z.array(z.string().min(1)).min(1) }),
).min(1)

export const buoyCite = z.object({
  /** Abschnitte der Anlage I der SeeSchStrO, z. B. "B.11". */
  see: citeGroup.optional(),
  /** Abschnitte in 33 CFR Part 62, z. B. "62.25". */
  cfr: citeGroup.optional(),
})

export const buoy = z.object({
  id: z.string().regex(/^[a-z0-9-]+$/),
  category: z.enum([
    'lateral',
    'preferred-channel',
    'cardinal',
    'isolated-danger',
    'safe-water',
    'special',
    'mooring',
  ]),
  /** Wo das Zeichen so gilt: IALA-Region A, Region B, oder in beiden gleich. */
  region: ialaRegion,
  name: bilingual,
  /** Farbfolge von oben nach unten. */
  colours: z.array(z.enum(['red', 'green', 'yellow', 'black', 'white', 'blue'])).min(1),
  /** Wie die Farben verteilt sind: waagerechte Bänder oder senkrechte Streifen. */
  pattern: z.enum(['bands', 'stripes']).default('bands'),
  topmark: z.enum([
    'none',
    'cylinder-red',
    'can-green',
    'cone-green',
    'cone-red',
    'cones-up',
    'cones-down',
    'cones-base',
    'cones-point',
    'spheres-black',
    'sphere-red',
    'cross-yellow',
  ]),
  shape: z.enum(['can', 'conical', 'pillar', 'spar', 'spherical', 'barrel']),
  /** Feuerkennung in IALA-Kurzschrift, fehlt bei unbefeuerten Zeichen. */
  character: z.string().optional(),
  meaning: bilingual,
  explanation: bilingual,
  /** Hinweis zur Geltung, wenn eine Quelle das Zeichen nicht führt. */
  regionNote: bilingual.optional(),
  cite: buoyCite,
})

/** Ein Bestandteil eines Schallsignals. */
export const signalElement = z
  .object({
    kind: z.enum(['short', 'prolonged', 'continuous', 'bell-rapid', 'bell-strokes', 'gong-rapid']),
    /** Wie oft hintereinander. */
    count: z.number().int().min(1).max(8).default(1),
  })
  .refine((e) => e.count === 1 || (e.kind !== 'continuous' && !e.kind.endsWith('rapid')), {
    message: 'Dauerton, Läuten und Gong gibt es nur einmal: ein Dauerton mit Pausen wäre keiner',
  })

export const signal = z.object({
  id: z.string().regex(/^[a-z0-9-]+$/),
  /** Regel aus Teil D (32 bis 37). */
  rule: z.number().int().min(32).max(37),
  cite: z.array(z.string().regex(/^(\d+:)?[a-z0-9()]+(:[a-z0-9()]+)?$/)).min(1),
  scope: scope.default('both'),
  name: bilingual,
  pattern: z.array(signalElement).min(1),
  /** Wiederholung in Sekunden, 0 = einmalig. */
  repeatS: z.number().min(0),
  meaning: bilingual,
  explanation: bilingual,
  context: z.enum(['manoeuvre', 'narrow-channel', 'warning', 'restricted-visibility', 'identity', 'distress']),
})

export const placement = z.object({
  /** Kurs über Grund in Grad, 0 = Nord, im Uhrzeigersinn. */
  headingDeg: z.number().min(0).max(360),
  /** Peilung vom eigenen Fahrzeug aus, relativ zum eigenen Kurs. Nur beim anderen. */
  bearingDeg: z.number().min(0).max(360).optional(),
  distanceNm: z.number().positive().optional(),
  /** Lichterzustand aus light-states.json. */
  state: z.string().regex(/^[a-z0-9-]+$/),
})

export const scenario = z.object({
  id: z.string().regex(/^[a-z0-9-]+$/),
  /** Regel aus Teil B (4 bis 19). */
  rule: z.number().int().min(4).max(19),
  cite: z.array(z.string().regex(/^(\d+:)?[a-z0-9()]+(:[a-z0-9()]+)?$/)).min(1),
  scope: scope.default('both'),
  title: bilingual,
  own: placement,
  other: placement,
  /** Wer ausweicht: das eigene Fahrzeug, das andere, beide, oder keines. */
  giveWay: z.enum(['own', 'other', 'both', 'neither']),
  /** Was zu tun ist, in einem Satz. */
  action: bilingual,
  explanation: bilingual,
  /**
   * Was man vom anderen Fahrzeug sehen muss. Wird gegen die Geometrie geprüft, damit
   * Bild und Aufgabe nicht auseinanderlaufen.
   */
  expectSeen: z.array(lightType).optional(),
  /**
   * Windrichtung, aus der er weht, in Grad. Nur bei Segelszenen: welcher Bug gilt, steht
   * nicht im Lichterbild, also gehört der Wind zur Aufgabe.
   */
  windFromDeg: z.number().min(0).max(360).optional(),
  tags: z.array(z.string().min(1)).default([]),
})

export const question = z.object({
  id: z.string().regex(/^[a-z0-9-]+$/),
  kind: z.enum(['light-state', 'buoy', 'scenario', 'signal']),
  ref: z.string().min(1),
  prompt: bilingual,
  answers: z.array(bilingual).min(2).max(6),
  correct: z.number().int().min(0),
  explanation: bilingual,
  rule: z.number().int().min(1).max(38),
  difficulty: z.number().int().min(1).max(3),
})

/**
 * Ein Lichterbild der Binnenschifffahrtsstraßen-Ordnung.
 *
 * Eigener Typ und nicht `lightState` mit einem Zusatzfeld: die Ordnung zählt in
 * Paragraphen statt in Regeln, kennt keine Unterscheidung international gegen inland und
 * liegt nur auf Deutsch vor. Ein gemeinsamer Typ mit zwei toten Hälften hätte am Ende
 * jeden Zustand betroffen, und die Vermischung der beiden Regelwerke ist genau der
 * Fehler, den dieser Block verhindern soll.
 */
export const binnenLightState = z.object({
  id: z.string().regex(/^b[a-z0-9-]+$/),
  /** Paragraph der Ordnung, etwa "3.08". */
  paragraph: z.string().regex(/^3\.\d{2}a?$/),
  /**
   * Fundstellen: "1" für Nummer 1 des eigenen Paragraphen, "1a" für Nummer 1 Buchstabe a,
   * "3.08:1a" für eine Stelle in einem anderen Paragraphen. Querverweise sind hier die
   * Regel und nicht die Ausnahme: die Ordnung baut ihre Bilder aufeinander auf, und
   * § 3.14 sagt ausdrücklich "zusätzlich zu der anderen vorgeschriebenen Bezeichnung".
   */
  cite: z.array(z.string().regex(/^(3\.\d{2}a?:)?\d+[a-z]?(\.\d+)?$/)).min(1),
  title: bilingual,
  vessel: z.string().regex(/^[a-z0-9-]+$/),
  underway: z.boolean(),
  makingWay: z.boolean(),
  lights: z.array(lightSpec),
  dayShapes: z.array(dayShape),
  /**
   * Nur auf Deutsch. Eine amtliche englische Fassung der Ordnung gibt es nicht, und eine
   * selbst übersetzte wäre eine Behauptung ohne Quelle.
   */
  erklaerung: z.string().min(40),
  tags: z.array(z.string().min(1)),
})

/**
 * Ein Bestandteil eines Binnen-Schallzeichens, nach der Vorbemerkung der Anlage 6.
 *
 * Eigene Arten und nicht die der Seesignale: der lange Ton dauert auf Binnenstraßen etwa
 * vier Sekunden, auf See vier bis sechs, und die Folge sehr kurzer Töne kennt die See
 * gar nicht. Ein gemeinsamer Typ hätte eine der beiden Ordnungen falsch klingen lassen.
 */
export const binnenSignalElement = z.object({
  kind: z.enum(['kurz', 'lang', 'sehr-kurz', 'glocke']),
  /** Wie oft hintereinander. Die Folge sehr kurzer Töne hat mindestens sechs. */
  count: z.number().int().min(1).max(12).default(1),
})

/**
 * Ein Schallzeichen der Anlage 6 der Binnenschifffahrtsstraßen-Ordnung.
 *
 * Wie die Lichterbilder der Ordnung nur auf Deutsch, bis auf den Namen: eine amtliche
 * englische Fassung gibt es nicht, und eine selbst übersetzte Bedeutung wäre eine
 * Behauptung ohne Quelle.
 */
export const binnenSignal = z.object({
  id: z.string().regex(/^bs-[a-z0-9-]+$/),
  /** Der Abschnitt der Anlage 6. */
  abschnitt: z.enum(['A', 'B', 'C', 'D', 'E', 'G']),
  /**
   * Die Fundstelle, wie die Anlage sie zitiert. Die Allgemeinen Zeichen in Abschnitt A
   * führen dort keine eigene: für sie gilt § 4.02 Nummer 1 mit der Anlage selbst.
   */
  fundstelle: z.string().min(4),
  name: bilingual,
  /** Leer bei "Kein Zeichen": beim Überholen ist das Schweigen die Zustimmung. */
  pattern: z.array(binnenSignalElement),
  /** Wiederholung in Sekunden, 0 = einmalig. */
  repeatS: z.number().min(0),
  /** Der Wortlaut der Anlage, ohne Anführungszeichen. */
  bedeutung: z.string().min(3),
  erklaerung: z.string().min(40),
  /** Die Lage aus der Zwischenüberschrift der Anlage. */
  lage: z.string().optional(),
  fall: z.enum(['normal', 'abweichung']).optional(),
  wer: z.enum(['bergfahrer', 'talfahrer', 'ueberholender', 'vorausfahrender']).optional(),
  /** Worauf das Zeichen antwortet: die id des Zeichens, das verlangt. */
  antwortAuf: z.string().regex(/^bs-[a-z0-9-]+$/).optional(),
  /** § 4.02 Nummer 2: ein Kleinfahrzeug darf nur die Allgemeinen Zeichen geben. */
  kleinfahrzeug: z.boolean(),
  /** § 4.01 Nummer 2: gelbes Lichtzeichen gleicher Dauer, nicht bei der Glocke. */
  lichtzeichen: z.boolean(),
})

export const vesselsFile = z.array(vesselType)
export const binnenSignalsFile = z.array(binnenSignal)
export const binnenLightStatesFile = z.array(binnenLightState)
export const lightStatesFile = z.array(lightState)
export const buoysFile = z.array(buoy)
export const signalsFile = z.array(signal)
export const scenariosFile = z.array(scenario)
export const questionsFile = z.array(question)

export type VesselTypeData = z.infer<typeof vesselType>
export type LightStateData = z.infer<typeof lightState>
export type BinnenLightStateData = z.infer<typeof binnenLightState>
export type BuoyData = z.infer<typeof buoy>
export type SignalData = z.infer<typeof signal>
export type BinnenSignalData = z.infer<typeof binnenSignal>
export type ScenarioData = z.infer<typeof scenario>
export type QuestionData = z.infer<typeof question>
